"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

/**
 * One confirmation dialog for every destructive action in the app.
 *
 * Deleting a business, a department, a worker, a task, a prompt, a key, an
 * invite — each used to ask its own way, or not at all. `useConfirm()`
 * returns a function that opens this dialog and resolves true only when the
 * person presses the red button. Escape, the backdrop and Cancel resolve
 * false. Cancel has focus when the dialog opens, so Enter never deletes.
 */
export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  /** The red button's label, e.g. "Delete worker". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Not destructive: the confirm button is violet instead of red. */
  neutral?: boolean;
}

type Ask = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Ask | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<
    (ConfirmOptions & { resolve: (ok: boolean) => void }) | null
  >(null);

  const ask = useCallback<Ask>(
    (opts) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...opts, resolve });
      }),
    []
  );

  function settle(ok: boolean) {
    setPending((cur) => {
      cur?.resolve(ok);
      return null;
    });
  }

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      {pending && (
        <Dialog
          {...pending}
          onCancel={() => settle(false)}
          onConfirm={() => settle(true)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Ask {
  const ask = useContext(ConfirmContext);
  if (!ask) {
    throw new Error("useConfirm must be used inside <ConfirmProvider>");
  }
  return ask;
}

function Dialog({
  title,
  message,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  neutral = false,
  onCancel,
  onConfirm,
}: ConfirmOptions & { onCancel: () => void; onConfirm: () => void }) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      } else if (e.key === "Tab") {
        // Two buttons; keep focus between them.
        const active = document.activeElement;
        const next = active === cancelRef.current ? confirmRef.current : cancelRef.current;
        e.preventDefault();
        next?.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="caw-confirm-title"
        className="w-full max-w-md rounded-xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl"
      >
        <h2 id="caw-confirm-title" className="text-base font-semibold text-zinc-100">
          {title}
        </h2>
        {message && <div className="mt-2 text-sm text-zinc-300">{message}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-zinc-700 px-4 py-2 text-sm text-zinc-300 transition hover:bg-zinc-800"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className={
              neutral
                ? "rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-violet-500"
                : "rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-500"
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
